import rubles from "./numberToWords";
import {capitalize} from "./notifyUser";

export let mobileMaxWidth = 760
export let mobileMaxWidthMediaQuery = `(max-width:${mobileMaxWidth}px)`

export function getCurrencyForm(amount: number, currency: 'RUB' | 'KZT' = 'RUB'): string {
    /**
     * Возвращает правильную форму валюты в зависимости от суммы
     * 
     * @param amount - Сумма (может быть с копейками)
     * @param currency - Валюта (RUB или KZT)
     * @returns Правильная форма валюты (рубль/рубля/рублей или тенге)
     */
    const currencyForms = {
        'RUB': ['рубль', 'рубля', 'рублей'],
        'KZT': ['тенге', 'тенге', 'тенге']
    }
    
    // Для тенге всегда возвращаем "тенге"
    if (currency === 'KZT') {
        return 'тенге'
    }
    
    // Для рублей определяем форму
    const integerPart = Math.floor(amount)
    const fractionalPart = amount - integerPart
    
    // Если есть копейки, всегда используем "рублей"
    if (fractionalPart > 0) {
        return 'рублей'
    }
    
    // Определяем правильную форму для рублей
    const lastDigit = integerPart % 10
    const lastTwoDigits = integerPart % 100
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'рублей'
    } else if (lastDigit === 1) {
        return 'рубль'
    } else if (lastDigit >= 2 && lastDigit <= 4) {
        return 'рубля'
    } else {
        return 'рублей'
    }
}

export function getPostfixForEntity(entity: any) {
    if (!entity) {
        return ""
    }

    let type = entity.type

    if (type == 'Дата' || type == 'Фото') {
        return ""
    }

    return getPostfix(entity.value, entity.needNumberToWords, entity.currency)
}

export function getPostfix(value: string, need: boolean, currency: 'RUB' | 'KZT' = 'RUB') {
    const cleanValue = value.trim().replace(/ /g, '')
    if (cleanValue.replace(/[^\d.,-]/g, '').length != cleanValue.length) {
        return ""
    }
    if (!need) {
        return ""
    }

    // Парсим число
    const num = parseFloat(cleanValue.replace(',', '.'))
    if (isNaN(num)) {
        return ""
    }

    // Получаем целую и дробную части
    const integerPart = Math.floor(num)
    const fractionalPart = Math.round((num - integerPart) * 100)

    // Формируем числительное только для целой части
    let result = rubles(integerPart.toString(), currency)
    if (result.length == 0) {
        return ""
    }

    // Скобки — только пропись. Валюта и копейки после них:
    // «(Шесть тысяч) рублей 50 копеек». Форма валюты — по целой части.
    const currencyText = getCurrencyForm(integerPart, currency)
    let suffix = ' ' + currencyText

    if (currency === 'RUB' && fractionalPart > 0) {
        const kopeckForms = ['копейка', 'копейки', 'копеек']
        const kopeckForm = getWordForm(fractionalPart, kopeckForms)
        suffix += ` ${fractionalPart.toString().padStart(2, '0')} ${kopeckForm}`
    }

    return ` (${capitalize(result)})${suffix}`
}

function getWordForm(num: number, forms: string[]): string {
    /**
     * Возвращает правильную форму слова в зависимости от числа
     */
    if (num % 10 === 1 && num % 100 !== 11) {
        return forms[0]
    } else if (num % 10 >= 2 && num % 10 <= 4 && (num % 100 < 10 || num % 100 >= 20)) {
        return forms[1]
    } else {
        return forms[2]
    }
}

export function userFullname(user: any): string {
    if (!user) {
        return ""
    }
    if (user.role == 'customer') {
        return (user.last_name ?? "") + " " + (user.first_name ?? "") + " " + (user.middle_name ?? "")
    } else {
        return user.law_name ?? ""
    }
}

export function signingSessionUserLabel(user: any): string {
    if (!user) {
        return ""
    }
    const name = userFullname(user).replace(/\s+/g, " ").trim()
    if (name) {
        return name
    }
    return String(user.phone_number || user.additional_phone_number || user.email || "").trim()
}

export function getEmployeeDisplayName(employee: any): string {
    console.log('getEmployeeDisplayName вызвана с:', employee);
    if (!employee) {
        console.log('Сотрудник пустой, возвращаем пустую строку');
        return "";
    }
    
    // Получаем данные пользователя из granted_to или из корня
    const userData = employee.granted_to || employee;
    const role = userData.role || employee.role;
    
    console.log('Роль сотрудника:', role);
    console.log('Данные пользователя:', userData);
    
    if (role === 'customer') {
        // Для физических лиц: фамилия + инициалы
        const lastName = userData.last_name || "";
        const firstName = userData.first_name || "";
        const middleName = userData.middle_name || "";
        
        console.log('ФИО из userData:', { lastName, firstName, middleName });
        
        let initials = "";
        if (firstName) {
            initials += firstName.charAt(0).toUpperCase() + ".";
        }
        if (middleName) {
            initials += middleName.charAt(0).toUpperCase() + ".";
        }
        
        const result = `${lastName} ${initials}`.trim();
        console.log('Результат для физ.лица:', result);
        return result;
    } else {
        // Для юридических лиц
        const result = userData.law_name || employee.law_name || "";
        console.log('Результат для юр.лица:', result);
        return result;
    }
}

export function getUserCardDisplayName(user: any, allCards: any[] = []): string {
    if (!user) return "";
    
    const name = userFullname(user);
    
    // Находим другие карточки с тем же именем, не учитывая ID
    const sameNameCount = allCards.filter(card => 
        card && userFullname(card) === name && card !== user
    ).length;
    
    // Если нет других карточек с таким же именем, возвращаем просто имя
    if (sameNameCount === 0) {
        return name;
    }
    
    // Получаем контактную информацию
    const userPhone = user.additional_phone_number ?? user.phone_number ?? '';
    const userEmail = user.email ?? '';
    
    // Если есть и телефон и почта, показываем оба
    if (userPhone && userEmail) {
        return `${name} (${userPhone}, ${userEmail})`;
    }
    
    // Если есть только телефон
    if (userPhone) {
        return `${name} (${userPhone})`;
    }
    
    // Если есть только почта
    if (userEmail) {
        return `${name} (${userEmail})`;
    }
    
    // Если нет ни телефона, ни почты
    return name;
}

export function safeArrayAccess<T>(arr: T[], index: number): T | undefined {
    if (index < 0) {
        return arr[arr.length + index];
    }
    return arr[index];
}

export function getLastElement<T>(arr: T[]): T | undefined {
    return arr.length > 0 ? arr[arr.length - 1] : undefined;
}
